import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
    mode: ThemeMode;
    isDark: boolean;
    setMode: (mode: ThemeMode) => void;
}

const STORAGE_KEY = 'mediflow-care-theme';

const ThemeContext = createContext<ThemeContextValue | null>(null);

function resolveIsDark(mode: ThemeMode): boolean {
    if (mode === 'dark') return true;
    if (mode === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [mode, setModeState] = useState<ThemeMode>(() => {
        try {
            return (localStorage.getItem(STORAGE_KEY) as ThemeMode) || 'system';
        } catch {
            return 'system';
        }
    });

    const [isDark, setIsDark] = useState(() => resolveIsDark(mode));

    useEffect(() => {
        const update = () => {
            const dark = resolveIsDark(mode);
            setIsDark(dark);
            document.documentElement.classList.toggle('dark', dark);
        };
        update();

        if (mode === 'system') {
            const mql = window.matchMedia('(prefers-color-scheme: dark)');
            mql.addEventListener('change', update);
            return () => mql.removeEventListener('change', update);
        }
    }, [mode]);

    const setMode = (newMode: ThemeMode) => {
        setModeState(newMode);
        try {
            localStorage.setItem(STORAGE_KEY, newMode);
        } catch { /* ignore */ }
    };

    return (
        <ThemeContext.Provider value={{ mode, isDark, setMode }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useThemeMode(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useThemeMode muss innerhalb von ThemeProvider verwendet werden');
    return ctx;
}
